/* eslint-disable jsx-a11y/anchor-is-valid */
import React, {useEffect, useRef, useState} from 'react'
import {useQuery} from 'react-query'
import ApexCharts, {ApexOptions} from 'apexcharts'
import {Graphic1Type} from '../core/_models'
import {getGraphic1} from '../core/_requests'
import {useAuth} from '../../../../app/modules/auth'
import {useMenuInnerContext} from "../../../../_metronic/layout/components/header/header-menus/MenuInnerContext"
import {resolveChartColor, withPremiumTheme} from './chart-theme'

type Props = {
  className: string
  chartColor: string
  chartHeight: string
}

const Graphic1: React.FC<Props> = ({ className, chartColor, chartHeight }) => {
  const {currentUser} = useAuth()
  const chartRef = useRef<HTMLDivElement | null>(null)
  const [refreshGraphic, setRefreshGraphic] = useState(false)

  const menuInnerContext = useMenuInnerContext()

  const query = useQuery(
    'boxes-vs-interventions',
    () => getGraphic1(currentUser!.last_company_id, menuInnerContext.dashboardStartDate, menuInnerContext.dashboardEndDate), {
    cacheTime: 0,
    refetchOnWindowFocus: false,
  })

  const refreshChart = () => {
    if (!chartRef.current || query.isFetching) {
      return
    }

    const chart1 = new ApexCharts(chartRef.current, chart1Options(chartColor, chartHeight, query.data))
    if (chart1) {
      chart1.render()
    }

    setRefreshGraphic(false)
    return chart1
  }

  useEffect(() => {
    query.refetch().then(() => {
      setRefreshGraphic(true)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, menuInnerContext.dashboardStartDate, menuInnerContext.dashboardEndDate])

  useEffect(() => {
    const chart1 = refreshChart()

    return () => {
      if (chart1) {
        chart1.destroy()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartRef, query.data, refreshGraphic])

  useEffect(() => {
    menuInnerContext.addFetchingGraphic('graphic1', query.isFetching)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.isFetching])

  return (
    <div className={className}>
      {(query.data && query.data.length > 0) ? (
        <div ref={chartRef} ></div>
      ) : (
        <div className='d-flex flex-1 justify-content-center align-items-center fs-7' style={{ color: '#9ca3af', minHeight: 180 }}>
          {query.isFetching ? (
            <div className="spinner-border text-secondary spinner-border-sm" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          ) : 'Sem dados'}
        </div>
      )}
    </div>
  )
}

const chart1Options = (chartColor: string, chartHeight: string, data: Graphic1Type[] | undefined): ApexOptions => {
  const baseColor = resolveChartColor(chartColor);

  return withPremiumTheme({
    series: [{
      name: 'Total de intervenções',
      data: data ? data.map((box) => box.total_opening) : []
    }],
    chart: {
      type: 'bar',
      height: chartHeight,
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: '60',
        borderRadius: 4,
      },
    },
    legend: { show: false },
    stroke: { show: false },
    xaxis: {
      categories: data ? data.sort((a, b) => b.total_opening - a.total_opening).map((box) => box.box_number) : [],
    },
    yaxis: {
      labels: {
        formatter: ((val) => val.toString()),
      },
    },
    fill: { opacity: 0.95 },
    tooltip: {
      x: { formatter: (val) => 'Caixa nº: ' + val },
      y: { formatter: (val) => val + ' veze(s)' },
    },
    colors: [baseColor],
  });
}

export { Graphic1 }
